import React, { useEffect, useRef, useState } from 'react'
import HomeHeader from '../components/HomePageHeader'
import Footer from '../components/Footer'
import GameCard from './components/GameCard'
import GameModal from './components/GameModal'
import { getGames } from '../services/games'
import { GAME_CATEGORIES } from '../constants'
import { resolveCategorySlug, slugifyCategory } from './categorySlugs'
import styles from './Games.module.css'

const PAGE_SIZE = 24

const CategoryPage = ({ category: slug, history }) => {
  const { title, category, sort, featured } = resolveCategorySlug(slug)

  const [games, setGames] = useState([])
  const [cursor, setCursor] = useState(undefined)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [activeSubdomain, setActiveSubdomain] = useState(null)

  // Bumped every time the category changes, so a "Load more" request kicked off for a previous
  // category (still in flight when the user switches categories) can tell it's been superseded
  // and drop its response instead of appending stale games/cursor onto the new category's list.
  const requestIdRef = useRef(0)

  useEffect(() => {
    requestIdRef.current += 1
    const requestId = requestIdRef.current

    setLoading(true)
    setLoadError(false)
    setGames([])
    setCursor(undefined)
    setLoadingMore(false)

    getGames({ category, sort, featured, limit: PAGE_SIZE })
      .then((data) => {
        if (requestIdRef.current !== requestId) return
        setGames(data?.games || [])
        setCursor(data?.nextCursor || undefined)
      })
      .catch(() => {
        if (requestIdRef.current === requestId) setLoadError(true)
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false)
      })
  }, [slug])

  const loadMore = async () => {
    const requestId = requestIdRef.current
    setLoadingMore(true)
    try {
      const data = await getGames({ category, sort, featured, limit: PAGE_SIZE, cursor })
      if (requestIdRef.current !== requestId) return
      setGames((prev) => [...prev, ...(data?.games || [])])
      setCursor(data?.nextCursor || undefined)
    } catch {
      if (requestIdRef.current === requestId) setLoadError(true)
    } finally {
      if (requestIdRef.current === requestId) setLoadingMore(false)
    }
  }

  return (
    <div className={`full-height ${styles.page}`}>
      <HomeHeader />

      <div className={styles.hero}>
        {/*<button className={styles.backBtn} onClick={() => history.push('/games')}>
          <i className="bi bi-arrow-left" /> Back to Games
        </button>*/}
        <h1 className={styles.heroTitle}>{title}</h1>
      </div>

      {/* Hide until we have multiple game submitted for different categories */}
      {/*<div className={styles.categoryTabs}>
        {GAME_CATEGORIES.map((c) => (
          <a
            key={c}
            href={`/games/${slugifyCategory(c)}`}
            className={`${styles.categoryTab} ${slugifyCategory(c) === slug ? styles.categoryTabActive : ''}`}
            onClick={(e) => {
              e.preventDefault()
              history.push(`/games/${slugifyCategory(c)}`)
            }}
          >
            {c}
          </a>
        ))}
      </div>*/}

      <div className={styles.body} style={{ paddingTop: 0 }}>
        <div className={styles.section}>
          {loading && <p className={styles.emptyState}>Loading games...</p>}
          {!loading && loadError && <p className={styles.emptyState}>Something went wrong loading games.</p>}
          {!loading && !loadError && !games.length && (
            <p className={styles.emptyState}>No games in this category yet.</p>
          )}
          <div className={styles.grid}>
            {games.map((game) => (
              <GameCard key={game.subdomain} game={game} onClick={(g) => setActiveSubdomain(g.subdomain)} />
            ))}
          </div>
          {cursor && (
            <button className={styles.loadMore} onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      </div>

      <GameModal subdomain={activeSubdomain} show={!!activeSubdomain} onHide={() => setActiveSubdomain(null)} />

      <Footer />
    </div>
  )
}

export default CategoryPage
